'use client';

import { useCallback, useEffect, useMemo, useReducer } from 'react';
import type { Project as ProjectSummary } from '@/types/project';
import type { ProjectFolder, ProjectFolderVisibility } from '@/components/home/theme-editor';
import { modalReducer, INITIAL_MODAL_STATE } from '@/contexts/DashboardContext.modal-reducer';

type ProjectContextMenuState = {
  project: ProjectSummary;
  x: number;
  y: number;
  view: 'main' | 'transfer' | 'folder';
};

interface UseDashboardModalsParams {
  activeWorkspaceId: string | undefined;
}

interface UseDashboardModalsResult {
  // ── Project Context Menu ──
  projectContextMenu: ProjectContextMenuState | null;
  openProjectContextMenu: (event: React.MouseEvent<HTMLElement>, project: ProjectSummary) => void;
  closeProjectContextMenu: () => void;

  // ── Delete/Rename Modals ──
  deleteModal: { isOpen: boolean; project: ProjectSummary | null };
  renameModal: { isOpen: boolean; project: ProjectSummary | null; value: string };
  openDeleteModal: (project: ProjectSummary) => void;
  closeDeleteModal: () => void;
  openRenameModal: (project: ProjectSummary) => void;
  closeRenameModal: () => void;
  setRenameModalValue: (value: string) => void;

  // ── Folder Management State ──
  createFolderModalOpen: boolean;
  folderEditorMode: 'create' | 'edit';
  folderEditorTarget: ProjectFolder | null;
  createFolderName: string;
  createFolderVisibility: ProjectFolderVisibility;
  folderDeleteTarget: ProjectFolder | null;
  folderAddProjectsTarget: ProjectFolder | null;
  createSubfolderTarget: ProjectFolder | null;
  moveFolderTarget: ProjectFolder | null;
  setCreateFolderName: (name: string) => void;
  setCreateFolderVisibility: (visibility: ProjectFolderVisibility) => void;

  // ── Folder Management Actions ──
  openCreateFolderModal: () => void;
  closeCreateFolderModal: () => void;
  openEditFolderModal: (folder: ProjectFolder) => void;
  openDeleteFolderModal: (folder: ProjectFolder) => void;
  closeDeleteFolderModal: () => void;
  openAddProjectsModal: (folder: ProjectFolder) => void;
  closeAddProjectsModal: () => void;
  openCreateSubfolderModal: (folder: ProjectFolder) => void;
  closeCreateSubfolderModal: () => void;
  openMoveFolderModal: (folder: ProjectFolder) => void;
  closeMoveFolderModal: () => void;
}

/**
 * `useDashboardModals` — owns the single `modalReducer`-backed state slot that
 * the dashboard uses for every modal/context-menu surface (delete, rename,
 * folder create/edit/delete/add-projects/subfolder/move, and the project
 * context menu).
 *
 * Extracted from `DashboardContext` (H1c-ADR-001 Sub-decision D) so the
 * dispatch wrappers, derived getters, and workspace-change close-all effect
 * live in one testable hook. The pure transition table stays in
 * `DashboardContext.modal-reducer.ts`; this hook only owns the React glue.
 *
 * `dispatch` is stable across renders (useReducer guarantee), so every wrapper
 * uses an empty dep array and keeps a stable function identity. The 3
 * object-shaped getters are memoized on `modal` (single dep) so identity is
 * stable across renders that don't change the modal state — required for the
 * consuming `value` useMemo's dep array.
 */
export function useDashboardModals({ activeWorkspaceId }: UseDashboardModalsParams): UseDashboardModalsResult {
  // ── Modal State (Sub-decision D, single useReducer) ─────────────────────────

  const [modal, dispatch] = useReducer(modalReducer, INITIAL_MODAL_STATE);

  // Close all modals and context menu when workspace changes to avoid stale references.
  // `dispatch` is stable (useReducer guarantee), so the deps list is exhaustive
  // with only `[activeWorkspace?.id]` — no `eslint-disable` needed.
  useEffect(() => {
    dispatch({ type: 'WORKSPACE_CHANGED' });
  }, [activeWorkspaceId]);

  // ── Modal handlers (thin dispatch wrappers) ──────────────────────────────────
  // All callbacks below dispatch to the modalReducer above. `dispatch` is stable
  // across renders (useReducer guarantee), so empty dep arrays are valid.

  const closeModal = useCallback(() => {
    dispatch({ type: 'CLOSE' });
  }, []);

  const openProjectContextMenu = useCallback((event: React.MouseEvent<HTMLElement>, project: ProjectSummary) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch({ type: 'OPEN_CONTEXT_MENU', project, x: event.clientX, y: event.clientY });
  }, []);

  const closeProjectContextMenu = closeModal;

  const openDeleteModal = useCallback((project: ProjectSummary) => {
    dispatch({ type: 'OPEN_DELETE', project });
  }, []);

  const closeDeleteModal = closeModal;

  const openRenameModal = useCallback((project: ProjectSummary) => {
    dispatch({ type: 'OPEN_RENAME', project });
  }, []);

  const closeRenameModal = closeModal;

  const setRenameModalValue = useCallback((value: string) => {
    dispatch({ type: 'SET_RENAME_VALUE', value });
  }, []);

  const openCreateFolderModal = useCallback(() => {
    dispatch({ type: 'OPEN_CREATE_FOLDER' });
  }, []);

  const closeCreateFolderModal = closeModal;

  const openEditFolderModal = useCallback((folder: ProjectFolder) => {
    dispatch({ type: 'OPEN_EDIT_FOLDER', folder });
  }, []);

  const setCreateFolderName = useCallback((name: string) => {
    dispatch({ type: 'SET_CREATE_FOLDER_NAME', name });
  }, []);

  const setCreateFolderVisibility = useCallback((visibility: ProjectFolderVisibility) => {
    dispatch({ type: 'SET_CREATE_FOLDER_VISIBILITY', visibility });
  }, []);

  const openDeleteFolderModal = useCallback((folder: ProjectFolder) => {
    dispatch({ type: 'OPEN_DELETE_FOLDER', folder });
  }, []);

  const closeDeleteFolderModal = closeModal;

  const openAddProjectsModal = useCallback((folder: ProjectFolder) => {
    dispatch({ type: 'OPEN_ADD_PROJECTS', folder });
  }, []);

  const closeAddProjectsModal = closeModal;

  const openCreateSubfolderModal = useCallback((folder: ProjectFolder) => {
    dispatch({ type: 'OPEN_CREATE_SUBFOLDER', folder });
  }, []);

  const closeCreateSubfolderModal = closeModal;

  const openMoveFolderModal = useCallback((folder: ProjectFolder) => {
    dispatch({ type: 'OPEN_MOVE_FOLDER', folder });
  }, []);

  const closeMoveFolderModal = closeModal;

  // ── Derived modal getters (preserve the legacy public API) ──────────────────
  // The reducer state is internal; consumers continue to read the same shape
  // they did before via these stable derivations. The 3 object-shaped getters
  // are memoized on the reducer state itself (single dep), so identity is
  // stable across renders that don't change `modal` — required for the value
  // useMemo's dep array to satisfy `react-hooks/exhaustive-deps` (§2.5).

  const projectContextMenu = useMemo<ProjectContextMenuState | null>(
    () => (modal.kind === 'contextMenu' ? { project: modal.project, x: modal.x, y: modal.y, view: modal.view } : null),
    [modal],
  );

  const deleteModal = useMemo<{ isOpen: boolean; project: ProjectSummary | null }>(
    () => (modal.kind === 'delete' ? { isOpen: true, project: modal.project } : { isOpen: false, project: null }),
    [modal],
  );

  const renameModal = useMemo<{ isOpen: boolean; project: ProjectSummary | null; value: string }>(
    () =>
      modal.kind === 'rename'
        ? { isOpen: true, project: modal.project, value: modal.value }
        : { isOpen: false, project: null, value: '' },
    [modal],
  );

  const createFolderModalOpen = modal.kind === 'createFolder';
  const folderEditorMode: 'create' | 'edit' = modal.kind === 'createFolder' ? modal.mode : 'create';
  const folderEditorTarget: ProjectFolder | null = modal.kind === 'createFolder' ? modal.target : null;
  const createFolderName = modal.kind === 'createFolder' ? modal.name : '';
  const createFolderVisibility: ProjectFolderVisibility =
    modal.kind === 'createFolder' ? modal.visibility : 'workspace';
  const folderDeleteTarget: ProjectFolder | null = modal.kind === 'deleteFolder' ? modal.folder : null;
  const folderAddProjectsTarget: ProjectFolder | null = modal.kind === 'addProjects' ? modal.folder : null;
  const createSubfolderTarget: ProjectFolder | null = modal.kind === 'createSubfolder' ? modal.folder : null;
  const moveFolderTarget: ProjectFolder | null = modal.kind === 'moveFolder' ? modal.folder : null;

  return {
    projectContextMenu,
    openProjectContextMenu,
    closeProjectContextMenu,
    deleteModal,
    renameModal,
    openDeleteModal,
    closeDeleteModal,
    openRenameModal,
    closeRenameModal,
    setRenameModalValue,
    createFolderModalOpen,
    folderEditorMode,
    folderEditorTarget,
    createFolderName,
    createFolderVisibility,
    folderDeleteTarget,
    folderAddProjectsTarget,
    createSubfolderTarget,
    moveFolderTarget,
    setCreateFolderName,
    setCreateFolderVisibility,
    openCreateFolderModal,
    closeCreateFolderModal,
    openEditFolderModal,
    openDeleteFolderModal,
    closeDeleteFolderModal,
    openAddProjectsModal,
    closeAddProjectsModal,
    openCreateSubfolderModal,
    closeCreateSubfolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
  };
}
